import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useResource } from "../lib/hooks";
import { Modal } from "./Modal";
import type {
    Employee,
    JobRole,
    LocationRow,
    Participant,
    Shift,
    ShiftEvaluation,
    ShiftTemplate,
} from "../lib/types";
import { useAuth } from "../context/AuthContext";

const MIN_REASON = 20;

const hoursOf = (from: string, to: string): number => {
    const [fh, fm] = from.split(":").map(Number);
    const [th, tm] = to.split(":").map(Number);
    let h = (th + tm / 60) - (fh + fm / 60);
    if (h <= 0) h += 24;
    return h;
};

interface Draft {
    employeeId: string;
    jobRoleId: string;
    placement: string; // "p:<id>" or "l:<id>"
    date: string;
    from: string;
    to: string;
    notes: string;
    /** Unassigned and open for staff to request — maps to status "open". */
    claim: boolean;
    spots: string;
    autoApprove: boolean;
}

interface Props {
    shift: Shift | null;
    initial: Partial<Draft>;
    employees: Employee[];
    roles: JobRole[];
    participants: Participant[];
    locations: LocationRow[];
    onClose: () => void;
    onSaved: () => void;
}

const toLocalDate = (iso: string): string => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const toLocalTime = (iso: string): string => {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/** Builds an offset-qualified instant so the server is never guessing a zone. */
const toInstant = (date: string, time: string): string => new Date(`${date}T${time}:00`).toISOString();

export const ShiftDrawer = ({
    shift, initial, employees, roles, participants, locations, onClose, onSaved,
}: Props) => {
    const { user } = useAuth();
    const canOverride = user?.role === "manager" || user?.role === "admin";

    const [draft, setDraft] = useState<Draft>({
        // Editing an open shift means employee_id is genuinely null — that
        // must stay blank, not fall through to "pick the first employee".
        employeeId: shift ? (shift.employee_id ?? "") : initial.employeeId ?? employees[0]?.id ?? "",
        jobRoleId: shift?.job_role_id ?? initial.jobRoleId ?? roles[0]?.id ?? "",
        placement: shift?.participant_id
            ? `p:${shift.participant_id}`
            : shift?.location_id
              ? `l:${shift.location_id}`
              : initial.placement ?? (participants[0] ? `p:${participants[0].id}` : ""),
        date: shift ? toLocalDate(shift.starts_at) : initial.date ?? new Date().toISOString().slice(0, 10),
        from: shift ? toLocalTime(shift.starts_at) : initial.from ?? "09:00",
        to: shift ? toLocalTime(shift.ends_at) : initial.to ?? "15:00",
        notes: shift?.notes ?? initial.notes ?? "",
        // Claim covers both an already-published open shift and a draft
        // that will become one on publish — an unassigned draft has no
        // other reason to exist.
        claim: shift ? (shift.status === "open" || (shift.status === "draft" && !shift.employee_id)) : false,
        spots: String(shift?.spots ?? initial.spots ?? 1),
        autoApprove: shift?.auto_approve_claims ?? false,
    });

    const [evaluation, setEvaluation] = useState<ShiftEvaluation | null>(null);
    const [reason, setReason] = useState(shift?.override_reason ?? "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const templates = useResource<ShiftTemplate[]>("/shift-templates");
    const [templateId, setTemplateId] = useState("");
    const [templateNameField, setTemplateNameField] = useState("");
    const [savingTemplate, setSavingTemplate] = useState(false);

    const payload = useCallback(() => {
        const [kind, id] = draft.placement.split(":");
        return {
            employeeId: draft.claim ? null : draft.employeeId || null,
            jobRoleId: draft.jobRoleId,
            participantId: kind === "p" ? id : null,
            locationId: kind === "l" ? id : null,
            startsAt: toInstant(draft.date, draft.from),
            endsAt: toInstant(draft.date, draft.to),
            notes: draft.notes.trim() || undefined,
            spots: draft.claim ? Math.max(1, Number(draft.spots) || 1) : undefined,
            autoApproveClaims: draft.claim ? draft.autoApprove : undefined,
        };
    }, [draft]);

    // Re-validate on every change. The engine is the same code the API runs, so
    // what is shown here is what the server will decide — with the database
    // trigger as the final authority behind both. Skipped for an open/claim
    // shift: there is no employee yet for the compliance rules to check.
    useEffect(() => {
        if (draft.claim || !draft.employeeId || !draft.jobRoleId || !draft.placement) return;
        let live = true;

        const suffix = shift ? `?excludeId=${shift.id}` : "";
        api.post<ShiftEvaluation>(`/shifts/validate${suffix}`, payload())
            .then((result) => { if (live) { setEvaluation(result); setError(null); } })
            .catch((err: unknown) => { if (live) setError(err instanceof Error ? err.message : "Validation failed"); });

        return () => { live = false; };
    }, [payload, shift, draft.claim, draft.employeeId, draft.jobRoleId, draft.placement]);

    const blocked = (evaluation?.blocking.length ?? 0) > 0;
    const absolute = (evaluation?.absolute.length ?? 0) > 0;
    const reasonTooShort = reason.trim().length < MIN_REASON;
    const needsOverride = blocked && !absolute;

    const canSave =
        !saving &&
        (draft.claim || (!!evaluation && !absolute && (!needsOverride || (canOverride && !reasonTooShort))));

    // A claim shift follows the same draft/publish rule as any other shift:
    // "Save draft" leaves it as a draft (invisible to staff, same as
    // always), and only publishing turns it into "open" — there's no
    // "confirmed" state to map to since nobody is assigned yet.
    const save = async (status: "confirmed" | "draft" = "confirmed") => {
        setSaving(true);
        setError(null);
        try {
            const effectiveStatus = draft.claim ? (status === "draft" ? "draft" : "open") : status;
            const body = { ...payload(), status: effectiveStatus, ...(needsOverride ? { overrideReason: reason.trim() } : {}) };
            if (shift) {
                await api.put(`/shifts/${shift.id}`, body);
            } else {
                await api.post("/shifts", body);
            }
            onSaved();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not save the shift");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!shift) return;
        setSaving(true);
        try {
            await api.delete(`/shifts/${shift.id}`);
            onSaved();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not cancel the shift");
        } finally {
            setSaving(false);
        }
    };

    const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
        setDraft((d) => ({ ...d, [key]: value }));

    const applyTemplate = (id: string) => {
        setTemplateId(id);
        const template = (templates.data ?? []).find((t) => t.id === id);
        if (!template) return;
        setDraft((d) => ({
            ...d,
            jobRoleId: template.job_role_id,
            placement: template.participant_id
                ? `p:${template.participant_id}`
                : template.location_id
                  ? `l:${template.location_id}`
                  : d.placement,
            from: template.starts_time.slice(0, 5),
            to: template.ends_time.slice(0, 5),
        }));
    };

    const saveAsTemplate = async () => {
        const name = templateNameField.trim();
        if (name.length < 2) return;
        setSavingTemplate(true);
        try {
            const [kind, id] = draft.placement.split(":");
            // The generic entity router (unlike this file's own /shifts calls)
            // expects the request body keyed by the actual column names.
            await api.post("/shift-templates", {
                name,
                job_role_id: draft.jobRoleId,
                participant_id: kind === "p" ? id : null,
                location_id: kind === "l" ? id : null,
                starts_time: draft.from,
                ends_time: draft.to,
            });
            setTemplateNameField("");
            templates.reload();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not save the template");
        } finally {
            setSavingTemplate(false);
        }
    };

    // Prototype's own rule for the primary button's label: a brand-new shift
    // or one still in draft says "Publish"; re-saving something already live
    // says "Save & publish". An override always says what it is, regardless.
    const alreadyLive = !!shift && shift.status !== "draft";
    const primaryLabel = saving
        ? "Saving…"
        : needsOverride
          ? "Override and save"
          : draft.claim
            ? (alreadyLive ? "Save changes" : "Publish open shift")
            : alreadyLive
              ? "Save & publish"
              : "Publish";

    const footer = (
        <>
            {shift && (
                <button className="danger-link" onClick={remove} disabled={saving}>
                    Delete shift
                </button>
            )}
            <button className="btn ghost" onClick={onClose} disabled={saving}>
                Cancel
            </button>
            <button
                className="btn ghost"
                onClick={() => void save("draft")}
                disabled={!canSave}
                title="Saved, but hidden from staff until you publish it"
            >
                Save draft
            </button>
            <button className="btn" onClick={() => void save("confirmed")} disabled={!canSave}>
                {primaryLabel}
            </button>
        </>
    );

    return (
        <Modal title={shift ? "Edit shift" : "New shift"} onClose={onClose} footer={footer}>
            {error && <div className="error-banner">{error}</div>}

            {(templates.data?.length ?? 0) > 0 && (
                <div className="field">
                    <label htmlFor="d-tpl">Start from template</label>
                    <select id="d-tpl" value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
                        <option value="">— blank shift —</option>
                        {(templates.data ?? []).map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                </div>
            )}

            <div className="form-grid">
                <div className="field half">
                    <label htmlFor="d-emp">Assign to</label>
                    <select id="d-emp" value={draft.employeeId} disabled={draft.claim}
                        onChange={(e) => set("employeeId", e.target.value)}>
                        {draft.claim
                            ? <option value="">— Open shift (unassigned) —</option>
                            : employees.map((e) => (
                                <option key={e.id} value={e.id}>
                                    {e.full_name}{e.employment_status !== "active" ? ` (${e.employment_status})` : ""}
                                </option>
                            ))}
                    </select>
                </div>

                <div className="field half">
                    <label htmlFor="d-role">Role required</label>
                    <select id="d-role" value={draft.jobRoleId} onChange={(e) => set("jobRoleId", e.target.value)}>
                        {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                </div>

                <div className="field">
                    <label htmlFor="d-place">Client / location</label>
                    <select id="d-place" value={draft.placement} onChange={(e) => set("placement", e.target.value)}>
                        <optgroup label="Participants">
                            {participants.map((p) => <option key={p.id} value={`p:${p.id}`}>{p.full_name}</option>)}
                        </optgroup>
                        <optgroup label="Locations">
                            {locations.map((l) => <option key={l.id} value={`l:${l.id}`}>{l.name}</option>)}
                        </optgroup>
                    </select>
                </div>

                <div className="field half">
                    <label htmlFor="d-date">Date</label>
                    <input id="d-date" type="date" value={draft.date} onChange={(e) => set("date", e.target.value)} />
                </div>
                <div className="field half" />

                <div className="field half">
                    <label htmlFor="d-from">Start</label>
                    <input id="d-from" type="time" value={draft.from} onChange={(e) => set("from", e.target.value)} />
                </div>
                <div className="field half">
                    <label htmlFor="d-to">
                        End <span style={{ float: "right", color: "var(--brand-2)", fontWeight: 700 }}>
                            {hoursOf(draft.from, draft.to).toFixed(1).replace(".0", "")} hrs
                        </span>
                    </label>
                    <input id="d-to" type="time" value={draft.to} onChange={(e) => set("to", e.target.value)} />
                </div>
            </div>

            <div className="field">
                <label htmlFor="d-notes">Notes for staff (optional)</label>
                <textarea id="d-notes" rows={2} value={draft.notes} onChange={(e) => set("notes", e.target.value)}
                    placeholder="Handover, gate code, parking…" />
            </div>

            <div className="field" style={{ background: "var(--accent-soft)", padding: "0.6rem 0.75rem", borderRadius: "var(--radius)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                    <input type="checkbox" id="d-claim" style={{ width: "auto" }}
                        checked={draft.claim} onChange={(e) => set("claim", e.target.checked)} />
                    <label htmlFor="d-claim" style={{ margin: 0, fontWeight: 400 }}>Allow staff to claim this shift</label>
                    {draft.claim && (
                        <>
                            <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "var(--muted)" }}>Spots</span>
                            <input type="number" min={1} max={20} value={draft.spots}
                                onChange={(e) => set("spots", e.target.value)} style={{ width: 64 }} />
                        </>
                    )}
                </div>
                {draft.claim && (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginTop: "0.5rem" }}>
                        <input type="checkbox" id="d-auto" style={{ width: "auto" }}
                            checked={draft.autoApprove} onChange={(e) => set("autoApprove", e.target.checked)} />
                        <label htmlFor="d-auto" style={{ margin: 0, fontWeight: 400 }}>
                            Auto-approve claims
                            <span style={{ display: "block", fontSize: "0.72rem", color: "var(--muted)", fontWeight: 400 }}>
                                The first eligible staff member to claim a spot is assigned instantly — no approval step.
                            </span>
                        </label>
                    </div>
                )}
            </div>

            <div className="field">
                <label htmlFor="d-tpl-name">Save this role, place and time as a template</label>
                <div style={{ display: "flex", gap: "0.4rem" }}>
                    <input
                        id="d-tpl-name"
                        value={templateNameField}
                        onChange={(e) => setTemplateNameField(e.target.value)}
                        placeholder="e.g. Rosewood AM (07:00–15:00)"
                        style={{ flex: 1 }}
                    />
                    <button
                        type="button"
                        className="btn ghost small"
                        disabled={savingTemplate || templateNameField.trim().length < 2}
                        onClick={() => void saveAsTemplate()}
                    >
                        {savingTemplate ? "Saving…" : "☆ Save"}
                    </button>
                </div>
            </div>

            {evaluation && (
                <>
                    <div className={`verdict-bar ${evaluation.verdict}`}>
                        <span className="word">
                            {evaluation.verdict === "block" ? "Blocked" : evaluation.verdict === "warn" ? "Allowed with flags" : "Pass"}
                        </span>
                        <span className="detail">
                            {evaluation.blocking.length > 0
                                ? evaluation.blocking.length === 1
                                    ? "1 rule refuses this shift"
                                    : `${evaluation.blocking.length} rules refuse this shift`
                                : evaluation.warnings.length > 0
                                  ? `${evaluation.warnings.length} flag${evaluation.warnings.length > 1 ? "s" : ""} recorded`
                                  : "Saves cleanly"}
                        </span>
                    </div>

                    <ul className="rules">
                        {evaluation.results.map((rule) => (
                            <li key={rule.id}>
                                <span className="rid">{rule.id}</span>
                                <span className={`chip ${rule.severity}`}>{rule.severity}</span>
                                <span className="why">{rule.message}</span>
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {absolute && (
                <div className="error-banner">
                    <strong>{evaluation!.absolute.join(" and ")}</strong> cannot be overridden by any role.
                    An expired mandatory competency and a participant exclusion are the two rules that
                    create direct harm risk — resolve the underlying record first.
                </div>
            )}

            {needsOverride && !canOverride && (
                <div className="error-banner">
                    Only a manager may override a compliance block. You are signed in as {user?.role}.
                </div>
            )}

            {needsOverride && canOverride && (
                <div className="field">
                    <label htmlFor="d-reason">Manager override — written justification</label>
                    <textarea
                        id="d-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why is this shift being staffed despite the block?"
                    />
                    <span style={{ fontSize: "0.75rem", color: reasonTooShort ? "var(--block)" : "var(--muted)" }}>
                        {reason.trim().length}/{MIN_REASON} characters. The shift stays flagged permanently and
                        an audit record is written.
                    </span>
                </div>
            )}
        </Modal>
    );
};
